import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth-context';
import { getShareThumbnailCache, type ShareThumbnailCache, type ShareThumbnailDescriptor, MAX_CACHED_SHARE_THUMBNAILS } from '@/lib/share-thumbnail-cache';

export interface ShareRowSummary {
  id: number;
  pulseId: number;
  shareType: 'preview' | 'final';
  hasUrl: boolean;
  expiresAt: string | null;
  revokedAt: string | null;
  isDraft: boolean;
  disableDownloads: boolean;
  internallyViewable: boolean;
  externalVisitCount: number;
  visitsCountedSince: string | null;
  thumbnail?: ShareThumbnailDescriptor;
}

interface Snapshot {
  summary: ShareRowSummary | null | undefined;
  error: string | null;
  updatedAt: number;
}

const EMPTY: Snapshot = { summary: undefined, error: null, updatedAt: 0 };
const SUMMARY_ROOT = '/api/preview-shares/by-pulse';
const MAX_BATCH = 50;
const FRESH_MS = 60_000;
const FOCUS_THROTTLE_MS = 15_000;
const summaryKey = (id: number) => [SUMMARY_ROOT, id, 'summary'] as const;

/**
 * One registry per authenticated QueryClient, not one request/observer per row.
 * Only intersecting mounted controls register. Duplicate email/share rows retain
 * one ID until their last visible control leaves. The old summary cache keys are
 * passive invalidation bridges for the share manager, never per-row queries.
 */
class ShareRowSummaryRegistry {
  private active = new Map<number, number>();
  private identities = new Map<number, object>();
  private revisions = new Map<number, number>();
  private snapshots = new Map<number, Snapshot>();
  private listeners = new Map<number, Set<() => void>>();
  private pending = new Set<number>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private poll: ReturnType<typeof setInterval> | undefined;
  private controller: AbortController | undefined;
  private running = false;
  private lastRefresh = 0;

  constructor(private client: QueryClient, private thumbnails: ShareThumbnailCache) {
    let epoch = thumbnails.epoch;
    // Keep this listener while idle too: hidden cached rows must also lose
    // protected content on lifecycle/permission changes.
    thumbnails.subscribe(() => {
      if (epoch === thumbnails.epoch) return;
      epoch = thumbnails.epoch;
      const ids = new Set([...this.snapshots.keys(), ...this.active.keys()]);
      this.snapshots.clear();
      for (const id of ids) {
        if (this.active.has(id)) this.revisions.set(id, (this.revisions.get(id) || 0) + 1);
        this.client.getQueryCache().find({ queryKey: summaryKey(id), exact: true })
          ?.setState({ data: undefined, dataUpdatedAt: 0 });
        this.emit(id);
        if (this.active.has(id)) this.pending.add(id);
      }
      if (this.active.size) this.schedule();
    });
  }

  getSnapshot = (id: number): Snapshot => {
    const existing = this.snapshots.get(id);
    if (existing) return existing;
    return EMPTY;
  };

  subscribe = (id: number, listener: () => void) => {
    const listeners = this.listeners.get(id) || new Set();
    listeners.add(listener);
    this.listeners.set(id, listeners);
    return () => {
      listeners.delete(listener);
      if (!listeners.size) this.listeners.delete(id);
    };
  };

  private emit(id: number) {
    this.listeners.get(id)?.forEach(listener => listener());
  }

  register(id: number): () => void {
    if (!this.active.has(id)) {
      this.identities.set(id, {});
      this.revisions.set(id, 0);
      // Install the passive bridge BEFORE scheduling the first request.
      // Otherwise a manager revocation while that request is in flight has no
      // cache key to invalidate, and its stale result could publish afterward.
      // It has no observer/queryFn and cannot start a per-row network fetch.
      const query = this.client.getQueryCache().build(this.client, {
        queryKey: summaryKey(id), gcTime: Infinity,
      });
      query.setOptions({ ...query.options, gcTime: Infinity });
    }
    this.active.set(id, (this.active.get(id) || 0) + 1);
    if (this.active.size === 1 && !this.poll) this.start();
    const snapshot = this.getSnapshot(id);
    const thumbnail = snapshot.summary && this.thumbnails.read(snapshot.summary.id);
    if (!snapshot.updatedAt || Date.now() - snapshot.updatedAt >= FRESH_MS ||
        (snapshot.summary?.thumbnail && (!thumbnail || Date.now() >= thumbnail.retryAt))) {
      this.pending.add(id);
      this.schedule();
    }
    return () => {
      const remaining = (this.active.get(id) || 1) - 1;
      if (remaining) {
        this.active.set(id, remaining);
      } else {
        this.active.delete(id);
        this.identities.delete(id);
        this.revisions.delete(id);
        this.pending.delete(id);
        // Leaving the viewport is not an invalidation. Retain bounded, fresh
        // summaries so scrolling back does not remove and recreate the image.
        if (!this.snapshots.has(id)) {
          this.client.removeQueries({ queryKey: summaryKey(id), exact: true });
        }
        this.prune();
      }
      if (!this.active.size) this.stop();
    };
  }

  private start() {
    this.armRefresh();
    window.addEventListener('focus', this.onFocus);
    document.addEventListener('visibilitychange', this.onFocus);
  }

  private stop() {
    clearTimeout(this.timer);
    clearTimeout(this.poll);
    this.timer = undefined;
    this.poll = undefined;
    this.controller?.abort();
    window.removeEventListener('focus', this.onFocus);
    document.removeEventListener('visibilitychange', this.onFocus);
  }

  private onFocus = () => this.refreshVisible(true);

  private prune() {
    // Active rows remain usable; bound additional retained offscreen rows.
    const inactive = [...this.snapshots.keys()].filter(id => !this.active.has(id));
    for (const id of inactive.slice(0, Math.max(0, inactive.length - MAX_CACHED_SHARE_THUMBNAILS))) {
      this.snapshots.delete(id);
      this.client.removeQueries({ queryKey: summaryKey(id), exact: true });
    }
  }

  private armRefresh() {
    clearTimeout(this.poll);
    if (!this.active.size) return;
    const now = Date.now();
    let delay = FRESH_MS;
    for (const id of this.active.keys()) {
      const snapshot = this.getSnapshot(id);
      if (snapshot.summary) {
        const thumbnail = this.thumbnails.read(snapshot.summary.id);
        if (thumbnail) delay = Math.min(delay, thumbnail.retryAt - now);
      }
      if (snapshot.updatedAt) delay = Math.min(delay, snapshot.updatedAt + FRESH_MS - now);
    }
    this.poll = setTimeout(() => {
      this.refreshVisible(false);
      this.armRefresh();
    }, Math.max(5_000, delay));
  }

  private refreshVisible(force: boolean) {
    if (document.visibilityState === 'hidden') return;
    const now = Date.now();
    if (force && now - this.lastRefresh < FOCUS_THROTTLE_MS) return;
    for (const id of this.active.keys()) {
      const snapshot = this.getSnapshot(id);
      const thumbnail = snapshot.summary && this.thumbnails.read(snapshot.summary.id);
      if (force || now - snapshot.updatedAt >= FRESH_MS ||
          (thumbnail && now >= thumbnail.retryAt)) this.pending.add(id);
    }
    if (this.pending.size) this.schedule();
  }

  private schedule() {
    if (this.timer || this.running) return;
    // Gather all rows mounted in this commit (including linked email copies).
    this.timer = setTimeout(() => {
      this.timer = undefined;
      void this.flush();
    }, 25);
  }

  private async flush() {
    if (this.running || !this.active.size || document.visibilityState === 'hidden') return;
    const ids = [...this.pending].filter(id => this.active.has(id)).sort((a, b) => a - b).slice(0, MAX_BATCH);
    if (!ids.length) return;
    ids.forEach(id => this.pending.delete(id));
    this.running = true;
    this.lastRefresh = Date.now();
    const requestedAt = Date.now();
    const epoch = this.thumbnails.epoch;
    const controller = new AbortController();
    this.controller = controller;
    // Capture identity as well as the ID: an old response must not populate a
    // newly mounted row after that ID became invisible and was registered again.
    const generations = new Map(ids.map(id => [id, this.identities.get(id)]));
    const revisions = new Map(ids.map(id => [id, this.revisions.get(id)]));
    const snapshots = new Map(ids.map(id => [id, this.getSnapshot(id)]));
    try {
      const res = await fetch(`/api/preview-shares/row-summaries?pulseIds=${ids.join(',')}`, {
        credentials: 'include', signal: controller.signal,
      });
      if (!res.ok) {
        if (!controller.signal.aborted && epoch === this.thumbnails.epoch &&
            (res.status === 401 || res.status === 403)) {
          for (const snapshot of snapshots.values()) {
            if (snapshot.summary) this.thumbnails.put(snapshot.summary.id, undefined, requestedAt, epoch);
          }
        }
        throw new Error(`Share details could not be loaded (${res.status}).`);
      }
      const body = await res.json();
      if (!Array.isArray(body.summaries)) throw new Error('Invalid share summary response.');
      const summaries = new Map<number, ShareRowSummary>(
        body.summaries.map((summary: ShareRowSummary) => [summary.pulseId, summary]),
      );
      for (const id of ids) {
        if (controller.signal.aborted || epoch !== this.thumbnails.epoch || this.identities.get(id) !== generations.get(id) ||
            this.revisions.get(id) !== revisions.get(id) || this.getSnapshot(id) !== snapshots.get(id)) continue;
        const summary = summaries.get(id) || null; // missing = inaccessible, not zero visits
        if (summary) {
          this.thumbnails.put(summary.id,
            !summary.isDraft && !summary.revokedAt && summary.internallyViewable ? summary.thumbnail : undefined,
            requestedAt, epoch);
        } else {
          const previous = snapshots.get(id)?.summary;
          if (previous) this.thumbnails.put(previous.id, undefined, requestedAt, epoch);
        }
        this.snapshots.delete(id);
        this.snapshots.set(id, { summary, error: null, updatedAt: Date.now() });
        this.client.setQueryData(summaryKey(id), summary);
        this.emit(id);
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        for (const id of ids) {
          if (this.identities.get(id) !== generations.get(id) ||
              this.revisions.get(id) !== revisions.get(id) || this.getSnapshot(id) !== snapshots.get(id)) continue;
          this.snapshots.set(id, {
            summary: undefined,
            error: error instanceof Error ? error.message : 'Share details could not be loaded.',
            updatedAt: Date.now(),
          });
          this.client.getQueryCache().find({ queryKey: summaryKey(id), exact: true })
            ?.setState({ data: undefined, dataUpdatedAt: 0 });
          this.emit(id);
        }
      }
    } finally {
      if (this.controller === controller) this.controller = undefined;
      this.running = false;
      this.prune();
      this.armRefresh();
      if (this.pending.size && this.active.size) this.schedule();
    }
  }
}

const registries = new WeakMap<ShareThumbnailCache, ShareRowSummaryRegistry>();

export function useShareRowSummary(pulseId: number) {
  const client = useQueryClient();
  const { user } = useAuth();
  const thumbnails = getShareThumbnailCache(client, `${user?.id ?? 'anonymous'}:${user?.role ?? ''}`);
  let registry = registries.get(thumbnails);
  if (!registry) {
    registry = new ShareRowSummaryRegistry(client, thumbnails);
    registries.set(thumbnails, registry);
  }
  const [node, setNode] = useState<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
  const subscribe = useCallback((listener: () => void) => registry.subscribe(pulseId, listener), [registry, pulseId]);
  const getSnapshot = useCallback(() => registry.getSnapshot(pulseId), [registry, pulseId]);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
  const shareId = snapshot.summary?.id;
  const thumbnailSnapshot = useCallback(() => shareId ? thumbnails.url(shareId) : null, [thumbnails, shareId]);
  const thumbnailUrl = useSyncExternalStore(thumbnails.subscribe, thumbnailSnapshot, () => null);

  useEffect(() => {
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      setVisible(entries.some(entry => entry.isIntersecting));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  useEffect(() => {
    if (!node || !visible || !user) return;
    return registry.register(pulseId);
  }, [registry, pulseId, node, visible, user?.id]);

  return { ...snapshot, thumbnailUrl, ref: setNode };
}